<template>
  <main>
    <div class="row">
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <b-form-input class="mr-1 mb-1" v-model="filterFormModel.keyword" v-on:keydown.enter="load"
                          :placeholder="$t('keyword')"></b-form-input>
            <b-form-select class="mr-1 mb-1" v-model="filterFormModel.status" @change="load">
              <option :value="undefined">{{ $t('all') }}</option>
              <option value="0">{{ $t('partnerDomain.enabled') }}</option>
              <option value="1">{{ $t('partnerDomain.disabled') }}</option>
            </b-form-select>
            <div class="mr-1 mb-1 pd-agency-filter" style="min-width:230px" v-if="canEditMeta">
              <admin-user-select :model="filterFormModel" attribute="agency" roleFilter="agency"
                                 :place-holder="$t('partnerDomain.agencyFilter')"></admin-user-select>
            </div>
            <button @click="load" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
            <button @click="reset" class="btn btn-secondary mr-1 mb-1">Reset</button>
          </div>
        </div>
      </div>
      <div class="col-12">
        <div class="c-sorting" v-if="canCreate">
          <div class="row">
            <div class="col-lg-12 c-sorting__right">
              <div class="c-sorting__btn">
                <button @click="create" class="btn btn-sm btn-success">
                  <font-awesome-icon icon="plus"/>&nbsp;{{ $t('create') }}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="itemCount">{{ $t('found', [itemCount]) }}</div>
          <div class="c-paging__right">
            <div class="c-paging__numbers">
              <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                            v-model="filterFormModel.page" :total-rows="itemCount"
                            :per-page="filterFormModel.pageSize" @input="load"></b-pagination>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12" v-if="items.length">
        <div class="pd-cards">
          <div class="pd-card" v-for="item in items" :key="item._id">
            <!-- Header: logo + tên + domain + trạng thái -->
            <div class="pd-card__head">
              <div class="pd-logo-box pd-logo-box--lg">
                <img v-if="item.branding && item.branding.logo" :src="item.branding.logo" alt="logo"/>
                <font-awesome-icon v-else icon="globe" class="text-muted" size="2x"/>
              </div>
              <div class="pd-card__title">
                <div class="pd-name">{{ item.name }}</div>
                <a :href="'https://' + item.domain" target="_blank" class="pd-domain-link">{{ item.domain }}</a>
              </div>
              <span class="pd-status ml-auto flex-shrink-0" :class="item.enabled ? 'pd-status--on' : 'pd-status--off'">
                <span class="pd-status-dot"></span>{{ item.enabled ? $t('partnerDomain.enabled') : $t('partnerDomain.disabled') }}
              </span>
            </div>

            <!-- Body: field có nhãn -->
            <div class="pd-card__body">
              <div class="pd-field">
                <span class="pd-field__label"><font-awesome-icon icon="user-tie"/> {{ $t('partnerDomain.agency') }}</span>
                <span class="pd-field__val" v-if="item.agency">{{ item.agency.name }}</span>
                <span class="pd-field__val text-danger" v-else>{{ $t('partnerDomain.noAgency') }}</span>
              </div>
              <div class="pd-field">
                <span class="pd-field__label"><font-awesome-icon icon="user-shield"/> AM</span>
                <span class="pd-field__val">{{ item.am ? item.am.name : '—' }}</span>
              </div>
              <div class="pd-field">
                <span class="pd-field__label"><font-awesome-icon icon="users"/> {{ $t('partnerDomain.clientCount') }}</span>
                <router-link :to="{ path: '/client', query: { partnerDomain: item._id } }" class="pd-field__val pd-link">{{ item.clientCount || 0 }} →</router-link>
              </div>
              <div class="pd-field">
                <span class="pd-field__label"><font-awesome-icon icon="envelope"/> Email</span>
                <span class="pd-field__val">{{ emailModeLabel(item) }}</span>
              </div>
              <!-- Tài khoản nhận tiền (chỉ khi bật thanh toán riêng) -->
              <div class="pd-field pd-field--wide" v-if="item.payment && item.payment.enabled">
                <span class="pd-field__label"><font-awesome-icon icon="credit-card"/> {{ $t('partnerDomain.paymentReceive') }}</span>
                <span class="pd-field__val"><b>{{ item.payment.bankName || item.payment.bankCode }}</b><template v-if="item.payment.accountNumber"> · {{ item.payment.accountNumber }}</template><span v-if="item.payment.accountHolder" class="text-muted"> · {{ item.payment.accountHolder }}</span></span>
              </div>
              <!-- Tính năng + màu thương hiệu -->
              <div class="pd-field pd-field--wide">
                <span class="pd-field__label"><font-awesome-icon icon="palette"/> {{ $t('partnerDomain.colConfig') }}</span>
                <span class="pd-field__val d-flex flex-wrap align-items-center" style="gap:5px">
                  <span v-if="item.app && (item.app.ios || item.app.android)" class="pd-chip pd-chip--app"><font-awesome-icon icon="mobile-alt"/> App</span>
                  <span v-if="item.livechat && item.livechat.embedCode" class="pd-chip pd-chip--chat"><font-awesome-icon icon="comments"/> Chat</span>
                  <span v-if="item.shareCustomersWithCallio" class="pd-chip pd-chip--share"><font-awesome-icon icon="user-shield"/> {{ $t('partnerDomain.shareLabel') }}</span>
                  <span v-for="c in activeColors(item)" :key="c" :title="item.branding[c]" class="pd-swatch" :style="{ background: item.branding[c] }"></span>
                  <span v-if="!activeColors(item).length && !(item.app && (item.app.ios || item.app.android)) && !(item.livechat && item.livechat.embedCode) && !item.shareCustomersWithCallio" class="text-muted small">—</span>
                </span>
              </div>
            </div>

            <!-- Footer: cảnh báo + hành động -->
            <div class="pd-card__foot">
              <div v-if="configWarnings(item).length" class="pd-warn" :title="configWarnings(item).join(' • ')">
                <font-awesome-icon icon="exclamation-triangle"/> {{ $t('partnerDomain.needAttention', [configWarnings(item).length]) }}
              </div>
              <div class="ml-auto" v-if="canWrite">
                <button @click="update(item._id)" type="button" class="btn btn-sm btn-info"><font-awesome-icon icon="edit"/> {{ $t('edit') }}</button>
                <button @click="del(item._id)" type="button" v-if="canDelete" class="btn btn-sm btn-outline-danger is-border-1 ml-1" :title="$t('delete')"><font-awesome-icon icon="trash"/></button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 text-center text-danger" v-if="!items.length && !listLoading">
        <span>{{ $t('notFoundSearch') }}</span>
      </div>
    </div>

    <b-modal v-model="showForm" size="xl" :title="formTitle" @ok="onSave" no-close-on-backdrop no-enforce-focus modal-class="pd-modal">
      <div class="pd-form">
        <b-tabs content-class="pt-3" no-fade class="pd-tabs">

          <b-tab active>
            <template #title><font-awesome-icon icon="globe" class="mr-1"/> {{ $t('partnerDomain.tabGeneral') }}</template>

        <!-- KHU VỰC 1: Thông tin chung -->
        <div class="pd-section">
          <div class="pd-section__title"><font-awesome-icon icon="globe"/> {{ $t('partnerDomain.secGeneral') }}</div>
          <!-- owner/admin: sửa đầy đủ field meta -->
          <template v-if="canEditMeta">
            <form-input :label="$t('partnerDomain.domainLabel')" :model="formModel" attribute="domain"
                        :errors="formErrors" placeholder="crm.doitac.com" :required="true"></form-input>
            <form-input :label="$t('partnerDomain.name')" :model="formModel" attribute="name"
                        :errors="formErrors" :required="true"></form-input>
            <admin-user-select :model="formModel" attribute="agency" roleFilter="agency"
                               :label="$t('partnerDomain.agency')" :required="true" :place-holder="$t('partnerDomain.chooseAgency')"
                               :errors="formErrors"></admin-user-select>
            <b-form-group :label="$t('partnerDomain.otpClient')" :description="$t('partnerDomain.otpClientDesc')">
              <client-select :model="formModel" attribute="client" :place-holder="$t('partnerDomain.otpClientPh')"></client-select>
            </b-form-group>
            <div class="row">
              <div class="col-md-6">
                <b-form-group :label="$t('partnerDomain.otpSmsTemplate')" :description="$t('partnerDomain.otpSmsTemplateDesc')">
                  <b-form-select v-model="formModel.otp.smsTemplate" :disabled="!formModel.client || otpTemplatesLoading"
                                 :options="otpSmsTemplates.map(t => ({ value: t._id, text: t.name + (t.brandName && t.brandName.name ? ' — ' + t.brandName.name : '') }))">
                    <template #first>
                      <b-form-select-option :value="null">{{ formModel.client ? $t('partnerDomain.otpTemplateNone') : $t('partnerDomain.otpChooseClientFirst') }}</b-form-select-option>
                    </template>
                  </b-form-select>
                </b-form-group>
              </div>
              <div class="col-md-6">
                <b-form-group :label="$t('partnerDomain.otpZnsTemplate')" :description="$t('partnerDomain.otpZnsTemplateDesc')">
                  <b-form-select v-model="formModel.otp.znsTemplate" :disabled="!formModel.client || otpTemplatesLoading"
                                 :options="otpZnsTemplates.map(t => ({ value: t._id, text: t.name + (t.refId ? ' (' + t.refId + ')' : '') }))">
                    <template #first>
                      <b-form-select-option :value="null">{{ formModel.client ? $t('partnerDomain.otpTemplateNone') : $t('partnerDomain.otpChooseClientFirst') }}</b-form-select-option>
                    </template>
                  </b-form-select>
                </b-form-group>
              </div>
            </div>
            <admin-user-select :model="formModel" attribute="am" roleFilter="am"
                               :label="$t('partnerDomain.am')" :place-holder="$t('partnerDomain.amPh')"
                               :errors="formErrors"></admin-user-select>
            <p class="small text-muted mt-n2 mb-2">{{ $t('partnerDomain.amDesc') }}</p>
            <form-checkbox :chk-label="$t('partnerDomain.enabledCheckbox')" :model="formModel" attribute="enabled"
                           :errors="formErrors"></form-checkbox>
          </template>
          <!-- agency: chỉ xem domain/tên, KHÔNG sửa -->
          <template v-else>
            <b-form-group :label="$t('partnerDomain.domainLabel')">
              <a :href="'https://' + formModel.domain" target="_blank"><b>{{ formModel.domain }}</b></a>
            </b-form-group>
            <b-form-group :label="$t('partnerDomain.name')">
              <div>{{ formModel.name }}</div>
            </b-form-group>
            <p class="small text-muted mb-0"><font-awesome-icon icon="info-circle"/> {{ $t('partnerDomain.agencyReadonlyNote') }}</p>
          </template>
        </div>

        <!-- Hướng dẫn trỏ DNS: đối tác tự tạo CNAME về fallback origin của Callio -->
        <div class="pd-section pd-dns">
          <div class="pd-section__title"><font-awesome-icon icon="globe"/> {{ $t('partnerDomain.secDns') }}</div>
          <p class="small text-muted mb-2">{{ $t('partnerDomain.dnsDesc') }}</p>
          <div class="pd-dns__row">
            <div class="pd-dns__cell"><span class="pd-dns__label">{{ $t('partnerDomain.dnsType') }}</span><code>CNAME</code></div>
            <div class="pd-dns__cell"><span class="pd-dns__label">{{ $t('partnerDomain.dnsName') }}</span><code>{{ formModel.domain || 'crm.doitac.com' }}</code></div>
            <div class="pd-dns__cell pd-dns__cell--grow">
              <span class="pd-dns__label">{{ $t('partnerDomain.dnsValue') }}</span>
              <code>{{ dnsTarget }}</code>
              <button type="button" class="btn btn-sm btn-outline-secondary is-border-1 ml-2"
                      v-clipboard:copy="dnsTarget" v-clipboard:success="onCopyDns" v-clipboard:error="onCopyDnsError">
                <font-awesome-icon icon="copy"/> {{ dnsCopied ? $t('partnerDomain.dnsCopied') : $t('partnerDomain.dnsCopy') }}
              </button>
            </div>
          </div>
          <ul class="small text-muted mb-0 pl-3">
            <li>{{ $t('partnerDomain.dnsNoteApex') }}</li>
            <li>{{ $t('partnerDomain.dnsNoteProxy') }}</li>
            <li>{{ $t('partnerDomain.dnsNoteTls') }}</li>
          </ul>
        </div>

          </b-tab>

          <b-tab>
            <template #title><font-awesome-icon icon="image" class="mr-1"/> {{ $t('partnerDomain.tabBrand') }}</template>

        <!-- KHU VỰC 2: Nhận diện thương hiệu (logo/favicon/tiêu đề) -->
        <div class="pd-section">
          <div class="pd-section__title"><font-awesome-icon icon="image"/> {{ $t('partnerDomain.secBrand') }}</div>
          <form-input :label="$t('partnerDomain.titleField')" :model="formModel.branding" attribute="title"
                      :errors="formErrors"></form-input>
          <b-form-group :label="'Logo'" :description="$t('partnerDomain.logoHint')">
            <div class="d-flex align-items-center">
              <img v-if="formModel.branding.logo" :src="formModel.branding.logo" alt="logo"
                   style="max-height:40px;margin-right:8px"/>
              <b-form-file accept="image/png,image/jpeg,image/jpg" :placeholder="$t('partnerDomain.choosePngJpg')"
                           @input="file => uploadAsset(file, 'logo')"></b-form-file>
              <button v-if="formModel.branding.logo" type="button" class="btn btn-sm btn-link text-danger"
                      @click="formModel.branding.logo = ''"><font-awesome-icon icon="trash-alt" :title="$t('partnerDomain.clear')"/></button>
            </div>
          </b-form-group>
          <b-form-group :label="'Favicon'" :description="$t('partnerDomain.faviconHint')">
            <div class="d-flex align-items-center">
              <img v-if="formModel.branding.favicon" :src="formModel.branding.favicon" alt="favicon"
                   style="max-height:24px;margin-right:8px"/>
              <b-form-file accept="image/png,image/jpeg,image/jpg" :placeholder="$t('partnerDomain.choosePngJpg')"
                           @input="file => uploadAsset(file, 'favicon')"></b-form-file>
              <button v-if="formModel.branding.favicon" type="button" class="btn btn-sm btn-link text-danger"
                      @click="formModel.branding.favicon = ''"><font-awesome-icon icon="trash-alt" :title="$t('partnerDomain.clear')"/></button>
            </div>
          </b-form-group>
        </div>

        <!-- KHU VỰC 3: Trang đăng nhập (banner + slogan) -->
        <div class="pd-section">
          <div class="pd-section__title"><font-awesome-icon icon="sign-in-alt"/> {{ $t('partnerDomain.secLogin') }}</div>
          <b-form-group :label="$t('partnerDomain.loginBanner')" :description="$t('partnerDomain.loginBannerDesc')">
            <div class="d-flex align-items-center">
              <img v-if="formModel.branding.loginBanner" :src="formModel.branding.loginBanner" alt="banner"
                   style="max-height:60px;margin-right:8px"/>
              <b-form-file accept="image/png,image/jpeg,image/jpg" :placeholder="$t('partnerDomain.choosePngJpg')"
                           @input="file => uploadAsset(file, 'loginBanner', 3)"></b-form-file>
              <button v-if="formModel.branding.loginBanner" type="button" class="btn btn-sm btn-link text-danger"
                      @click="formModel.branding.loginBanner = ''"><font-awesome-icon icon="trash-alt" :title="$t('partnerDomain.clear')"/></button>
            </div>
          </b-form-group>
          <b-form-group :label="$t('partnerDomain.loginSlogan')" :description="$t('partnerDomain.loginSloganDesc')">
            <b-form-textarea v-model="formModel.branding.loginSlogan" rows="2" max-rows="4"
                             :placeholder="sloganPlaceholder"></b-form-textarea>
          </b-form-group>
        </div>

        <!-- KHU VỰC 4: Màu sắc giao diện dashboard -->
        <div class="pd-section">
          <div class="pd-section__title"><font-awesome-icon icon="palette"/> {{ $t('partnerDomain.secColors') }}</div>
          <p class="small text-muted">{{ $t('partnerDomain.colorsHint') }}</p>
          <div class="row">
            <div class="col-md-6" v-for="c in colorFields" :key="c">
              <b-form-group :label="$t('partnerDomain.' + c)">
                <div class="d-flex align-items-center">
                  <input type="color" :value="formModel.branding[c] || '#000000'"
                         @input="e => formModel.branding[c] = e.target.value" style="width:42px;height:32px;margin-right:8px"/>
                  <b-form-input v-model="formModel.branding[c]" placeholder="#RRGGBB" style="max-width:120px"></b-form-input>
                  <button v-if="formModel.branding[c]" type="button" class="btn btn-sm btn-link text-danger"
                          @click="formModel.branding[c] = ''"><font-awesome-icon icon="trash-alt" :title="$t('partnerDomain.clear')"/></button>
                </div>
              </b-form-group>
            </div>
          </div>
        </div>

          </b-tab>

          <b-tab v-if="canEditConfig">
            <template #title><font-awesome-icon icon="link" class="mr-1"/> {{ $t('partnerDomain.tabLinks') }}</template>

        <!-- KHU VỰC 5: Liên kết & liên hệ (owner/admin) — RỖNG = ẨN link trên domain đối tác -->
        <div class="pd-section" v-if="canEditConfig">
          <div class="pd-section__title"><font-awesome-icon icon="link"/> {{ $t('partnerDomain.secLinks') }}</div>
          <p class="small text-muted">{{ $t('partnerDomain.linksDesc') }}</p>
          <div class="row">
            <div class="col-md-6"><form-input :label="$t('partnerDomain.lTerms')" :model="formModel.links" attribute="terms" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.lPrivacy')" :model="formModel.links" attribute="privacy" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.lGuide')" :model="formModel.links" attribute="guide" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.lSupport')" :model="formModel.links" attribute="support" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.lWebsite')" :model="formModel.links" attribute="website" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.lHotline')" :model="formModel.links" attribute="hotline" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.lContactEmail')" :model="formModel.links" attribute="contactEmail" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.lPricing')" :model="formModel.links" attribute="pricing" :errors="formErrors" placeholder="https://doitac.com/bang-gia"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.appName')" :model="formModel" attribute="appName" :errors="formErrors"></form-input></div>
          </div>
        </div>

          </b-tab>

          <b-tab v-if="canEditConfig">
            <template #title><font-awesome-icon icon="envelope" class="mr-1"/> {{ $t('partnerDomain.tabEmail') }}</template>

        <!-- KHU VỰC 6: Email (owner/admin) -->
        <div class="pd-section" v-if="canEditConfig">
          <div class="pd-section__title"><font-awesome-icon icon="envelope"/> {{ $t('partnerDomain.secEmail') }}</div>
          <p class="small text-muted">{{ $t('partnerDomain.emailDesc') }}</p>
          <div class="row">
            <div class="col-md-6"><form-input :label="$t('partnerDomain.eFromName')" :model="formModel.email" attribute="fromName" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.eLogoUrl')" :model="formModel.email" attribute="logoUrl" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.eCompanyName')" :model="formModel.email" attribute="companyName" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.eCompanyAddress')" :model="formModel.email" attribute="companyAddress" :errors="formErrors"></form-input></div>
          </div>
          <b-form-group :label="$t('partnerDomain.eFooter')">
            <b-form-textarea v-model="formModel.email.footerHtml" rows="2" max-rows="6" :placeholder="$t('partnerDomain.eFooterPh')"></b-form-textarea>
          </b-form-group>
          <!-- Cách gửi email hệ thống (3 mode): callio mặc định / Sender Signature / SMTP đối tác -->
          <div class="pd-section__sub mt-2">
            <div class="small font-weight-bold mb-2"><font-awesome-icon icon="paper-plane"/> {{ $t('partnerDomain.sendMode') }}</div>
            <b-form-radio-group v-model="formModel.email.mode" stacked>
              <b-form-radio value="callio">{{ $t('partnerDomain.modeCallio') }}</b-form-radio>
              <b-form-radio value="signature">{{ $t('partnerDomain.modeSignature') }}</b-form-radio>
              <b-form-radio value="smtp">{{ $t('partnerDomain.modeSmtp') }}</b-form-radio>
            </b-form-radio-group>

            <!-- mode=callio -->
            <div class="alert alert-light border small mt-2 mb-0" v-if="emailMode === 'callio'">{{ $t('partnerDomain.modeCallioHint') }}</div>

            <!-- mode=signature: Sender Signature (Postmark xác nhận địa chỉ đối tác) -->
            <div class="mt-3" v-if="emailMode === 'signature'">
              <div class="small font-weight-bold mb-1">
                {{ $t('partnerDomain.senderTitle') }}
                <span class="badge ml-1" :class="senderBadgeClass" v-if="formModel.id">{{ senderStatusLabel }}</span>
              </div>
              <div class="row">
                <div class="col-md-8"><form-input :label="$t('partnerDomain.eFromAddress')" :model="formModel.email" attribute="fromAddress" :errors="formErrors"></form-input></div>
              </div>
              <div class="alert alert-light border small mb-2" style="white-space:pre-line">{{ $t('partnerDomain.senderGuide') }}</div>
              <template v-if="formModel.id">
                <button type="button" class="btn btn-sm btn-primary mr-1 mb-1" :disabled="senderActing" @click="senderConfirm">
                  <font-awesome-icon icon="paper-plane"/> {{ $t('partnerDomain.senderSend') }}
                </button>
                <button type="button" class="btn btn-sm btn-outline-primary is-border-1 mr-1 mb-1" :disabled="senderActing" @click="senderCheck">
                  <font-awesome-icon icon="sync"/> {{ $t('partnerDomain.senderCheck') }}
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary is-border-1 mb-1" :disabled="senderActing || senderStatus === 'none'" @click="senderResend">
                  {{ $t('partnerDomain.senderResend') }}
                </button>
              </template>
              <div class="small text-muted" v-else>{{ $t('partnerDomain.senderSaveFirst') }}</div>
            </div>

            <!-- mode=smtp: gửi qua mail server riêng của đối tác -->
            <div class="mt-3" v-if="emailMode === 'smtp'">
              <div class="small font-weight-bold mb-1">{{ $t('partnerDomain.smtpTitle') }}</div>
              <div class="row">
                <div class="col-md-6">
                  <b-form-group :label="$t('partnerDomain.smtpHost')">
                    <b-form-input v-model="formModel.email.smtp.host" placeholder="smtp.congty.vn"></b-form-input>
                  </b-form-group>
                </div>
                <div class="col-md-3">
                  <b-form-group :label="$t('partnerDomain.smtpPort')">
                    <b-form-input type="number" v-model.number="formModel.email.smtp.port" placeholder="587"></b-form-input>
                  </b-form-group>
                </div>
                <div class="col-md-3">
                  <b-form-group :label="$t('partnerDomain.smtpSecure')">
                    <b-form-checkbox v-model="formModel.email.smtp.secure" switch class="mt-2">SSL (465)</b-form-checkbox>
                  </b-form-group>
                </div>
                <div class="col-md-6">
                  <b-form-group :label="$t('partnerDomain.smtpUser')">
                    <b-form-input v-model="formModel.email.smtp.user" placeholder="info@congty.vn"></b-form-input>
                  </b-form-group>
                </div>
                <div class="col-md-6">
                  <b-form-group :label="$t('partnerDomain.smtpPass')">
                    <b-input-group>
                      <b-form-input :type="smtpPassVisible ? 'text' : 'password'" autocomplete="new-password"
                                    v-model="formModel.email.smtp.pass"
                                    :placeholder="formModel.email.smtp.hasPass ? $t('partnerDomain.smtpPassSet') : ''"></b-form-input>
                      <b-input-group-append>
                        <button type="button" class="btn btn-outline-secondary is-border-1"
                                :title="smtpPassVisible ? $t('partnerDomain.smtpPassHide') : $t('partnerDomain.smtpPassShow')"
                                @click="smtpPassVisible = !smtpPassVisible">
                          <font-awesome-icon :icon="smtpPassVisible ? 'eye-slash' : 'eye'"/>
                        </button>
                      </b-input-group-append>
                    </b-input-group>
                  </b-form-group>
                </div>
              </div>
              <div class="alert alert-light border small mb-2" style="white-space:pre-line">{{ $t('partnerDomain.smtpGuide') }}</div>
              <template v-if="formModel.id">
                <div class="d-flex align-items-center flex-wrap">
                  <b-form-input v-model="smtpTestTo" :placeholder="$t('partnerDomain.smtpTestTo')" style="max-width:260px" class="mr-2 mb-1"></b-form-input>
                  <button type="button" class="btn btn-sm btn-primary mb-1" :disabled="smtpTesting" @click="smtpTest">
                    <font-awesome-icon icon="paper-plane"/> {{ $t('partnerDomain.smtpTest') }}
                  </button>
                </div>
                <div class="small mt-1" :class="smtpTestOk ? 'text-success' : 'text-danger'" v-if="smtpTestMsg">{{ smtpTestMsg }}</div>
              </template>
              <div class="small text-muted" v-else>{{ $t('partnerDomain.senderSaveFirst') }}</div>
            </div>
          </div>
        </div>

          </b-tab>

          <b-tab v-if="canEditConfig">
            <template #title><font-awesome-icon icon="comments" class="mr-1"/> {{ $t('partnerDomain.tabLivechatApp') }}</template>

        <!-- KHU VỰC 7: Livechat (owner/admin) — code nhúng đầy đủ; rỗng = không có livechat -->
        <div class="pd-section" v-if="canEditConfig">
          <div class="pd-section__title"><font-awesome-icon icon="comments"/> {{ $t('partnerDomain.secLivechat') }}</div>
          <b-form-group :label="$t('partnerDomain.livechatCode')" :description="$t('partnerDomain.livechatDesc')">
            <b-form-textarea v-model="formModel.livechat.embedCode" rows="3" max-rows="12" class="text-monospace"
                             :placeholder="$t('partnerDomain.livechatPh')"></b-form-textarea>
          </b-form-group>
          <b-form-group :label="$t('partnerDomain.livechatAppUrl')" :description="$t('partnerDomain.livechatAppUrlDesc')">
            <b-form-input v-model="formModel.livechat.appUrl" placeholder="https://..."></b-form-input>
          </b-form-group>
        </div>

        <!-- KHU VỰC 9: App mobile (owner/admin) — link store đối tác; rỗng = ẩn popup tải app -->
        <div class="pd-section" v-if="canEditConfig">
          <div class="pd-section__title"><font-awesome-icon icon="mobile-alt"/> {{ $t('partnerDomain.secApp') }}</div>
          <p class="small text-muted">{{ $t('partnerDomain.appDesc') }}</p>
          <div class="row">
            <div class="col-md-6"><form-input :label="$t('partnerDomain.appIos')" :model="formModel.app" attribute="ios" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.appAndroid')" :model="formModel.app" attribute="android" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.appIosVersion')" :model="formModel.app" attribute="iosVersion" :errors="formErrors" placeholder="1.0.0"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.appAndroidVersion')" :model="formModel.app" attribute="androidVersion" :errors="formErrors" placeholder="1.0.0"></form-input></div>
          </div>
        </div>

          </b-tab>

          <b-tab v-if="canEditConfig || canEditMeta">
            <template #title><font-awesome-icon icon="credit-card" class="mr-1"/> {{ $t('partnerDomain.tabPayment') }}</template>

        <!-- KHU VỰC 8: Chia sẻ khách cho Callio (owner/admin) -->
        <div class="pd-section" v-if="canEditMeta">
          <div class="pd-section__title"><font-awesome-icon icon="user-shield"/> {{ $t('partnerDomain.secShare') }}</div>
          <form-checkbox :chk-label="$t('partnerDomain.shareLabel')" :model="formModel" attribute="shareCustomersWithCallio" :errors="formErrors"></form-checkbox>
          <p class="small text-muted mb-0">{{ $t('partnerDomain.shareDesc') }}</p>
        </div>

        <!-- KHU VỰC 9: Tự mua / tự thanh toán (owner/admin) — tắt = khách phải liên hệ đối tác -->
        <div class="pd-section" v-if="canEditMeta">
          <div class="pd-section__title"><font-awesome-icon icon="lock"/> {{ $t('partnerDomain.secSelfPurchase') }}</div>
          <form-checkbox :chk-label="$t('partnerDomain.selfPurchaseLabel')" :model="formModel" attribute="selfPurchase" :errors="formErrors"></form-checkbox>
          <p class="small text-muted mb-0">{{ $t('partnerDomain.selfPurchaseDesc') }}</p>
        </div>

        <!-- KHU VỰC 10: Thanh toán đối tác (owner/admin) — tiền về TK đối tác; Billing + QR ăn theo -->
        <div class="pd-section" v-if="canEditConfig">
          <div class="pd-section__title"><font-awesome-icon icon="credit-card"/> {{ $t('partnerDomain.secPayment') }}</div>
          <form-checkbox :chk-label="$t('partnerDomain.paymentEnabled')" :model="formModel.payment" attribute="enabled" :errors="formErrors"></form-checkbox>
          <p class="small text-muted">{{ $t('partnerDomain.paymentDesc') }}</p>
          <div class="row" v-if="formModel.payment.enabled">
            <div class="col-md-6">
              <b-form-group :label="$t('partnerDomain.paymentBank')">
                <b-form-select v-model="formModel.payment.bankCode" @change="onBankChange">
                  <option value="">— {{ $t('partnerDomain.paymentChooseBank') }} —</option>
                  <option v-for="b in banks" :key="b.code" :value="b.code">{{ b.name }}</option>
                </b-form-select>
              </b-form-group>
            </div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.paymentBranch')" :model="formModel.payment" attribute="branch" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.paymentHolder')" :model="formModel.payment" attribute="accountHolder" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.paymentNumber')" :model="formModel.payment" attribute="accountNumber" :errors="formErrors"></form-input></div>
            <div class="col-md-6">
              <form-input :label="$t('partnerDomain.paymentPrefix')" :model="formModel.payment" attribute="contentPrefix" :errors="formErrors"></form-input>
              <p class="small text-muted">{{ $t('partnerDomain.paymentPrefixHint') }}</p>
            </div>
          </div>
        </div>
          </b-tab>

        </b-tabs>
      </div>
    </b-modal>
  </main>
</template>

<script>
import List from '../../mixins/List';
import CRUD from "../../mixins/CRUD";
import AdminUserSelect from "@/components/AdminUserSelect";
import ClientSelect from "@/components/ClientSelect";

function emptyBranding() {
  return { logo: '', favicon: '', primaryColor: '', secondaryColor: '', sidebarBgColor: '', headerBgColor: '', title: '', loginBanner: '', loginSlogan: '' };
}
function emptyLinks() {
  return { terms: '', privacy: '', guide: '', support: '', website: '', hotline: '', contactEmail: '', pricing: '' };
}
function emptyEmail() {
  return {
    fromName: '', fromAddress: '', logoUrl: '', footerHtml: '', companyName: '', companyAddress: '',
    senderStatus: 'none', senderSignatureId: null,
    mode: 'callio',
    smtp: { host: '', port: 587, secure: false, user: '', pass: '', hasPass: false }
  };
}
function emptyApp() {
  return { ios: '', android: '', iosVersion: '', androidVersion: '' };
}
function emptyPayment() {
  return { enabled: false, bankCode: '', bankName: '', branch: '', accountHolder: '', accountNumber: '', contentPrefix: '' };
}

export default {
  components: { AdminUserSelect, ClientSelect },
  mixins: [List, CRUD],
  data: function () {
    return {
      service: '/partner-domain',
      autoLoad: true,
      senderActing: false,
      smtpTesting: false,
      smtpTestTo: '',
      smtpTestOk: false,
      smtpTestMsg: '',
      sloganPlaceholder: 'Phần mềm quản lý kinh doanh tập trung\nThúc đẩy tăng trưởng thật',
      colorFields: ['primaryColor', 'secondaryColor', 'sidebarBgColor', 'headerBgColor'],
      // Mã ngân hàng VietQR (img.vietqr.io chấp nhận short code) — danh sách NH phổ biến VN
      banks: [
        { code: 'VCB', name: 'Vietcombank' }, { code: 'MB', name: 'TMCP Quân đội (MBBank)' },
        { code: 'TCB', name: 'Techcombank' }, { code: 'BIDV', name: 'BIDV' },
        { code: 'ICB', name: 'VietinBank' }, { code: 'ACB', name: 'ACB' },
        { code: 'VPB', name: 'VPBank' }, { code: 'TPB', name: 'TPBank' },
        { code: 'STB', name: 'Sacombank' }, { code: 'VIB', name: 'VIB' },
        { code: 'SHB', name: 'SHB' }, { code: 'HDB', name: 'HDBank' },
        { code: 'MSB', name: 'MSB' }, { code: 'OCB', name: 'OCB' },
        { code: 'SCB', name: 'SCB' }, { code: 'VBA', name: 'Agribank' },
        { code: 'EIB', name: 'Eximbank' }, { code: 'SeAB', name: 'SeABank' },
        { code: 'NAB', name: 'Nam A Bank' }, { code: 'BAB', name: 'Bac A Bank' },
        { code: 'LPB', name: 'LPBank' }, { code: 'PVCB', name: 'PVcomBank' },
        { code: 'CAKE', name: 'CAKE by VPBank' }, { code: 'VRB', name: 'VRB' }
      ],
      defaultFormModel: {
        domain: null,
        name: null,
        agency: null,
        client: null,
        am: null,
        otp: { smsTemplate: null, znsTemplate: null },
        enabled: true,
        appName: '',
        shareCustomersWithCallio: true,
        selfPurchase: true,
        branding: emptyBranding(),
        links: emptyLinks(),
        email: emptyEmail(),
        livechat: { embedCode: '', appUrl: '' },
        app: emptyApp(),
        payment: emptyPayment()
      },
      // Template OTP (SMS/ZNS) của client đại lý đang chọn — nạp qua /partner-domain/otp-templates
      otpSmsTemplates: [],
      otpZnsTemplates: [],
      otpTemplatesLoading: false,
      dnsCopied: false,
      smtpPassVisible: false
    }
  },
  watch: {
    // Đổi client đại lý → nạp lại danh sách template OTP tương ứng
    'formModel.client': function (val) {
      this.loadOtpTemplates(val);
    },
    // Mở bản ghi khác → ẩn lại mật khẩu, tránh lộ pass domain vừa xem
    'formModel.id': function () {
      this.smtpPassVisible = false;
    }
  },
  computed: {
    // Đích CNAME đối tác phải trỏ về (fallback origin, cert cấp tự động theo custom hostname)
    dnsTarget() {
      return process.env.VUE_APP_PARTNER_DNS_TARGET || 'fallback.callio.vn';
    },
    // agency chỉ sửa branding domain của mình → KHÔNG tạo/xoá, KHÔNG sửa field meta.
    isAgency() {
      return this.$store.state.user.role === 'agency';
    },
    canEditMeta() {
      // Chỉ owner/admin: sửa Thông tin chung (domain/name/agency) + Chia sẻ khách
      return ['owner', 'admin'].includes(this.$store.state.user.role);
    },
    canEditConfig() {
      // owner/admin + agency + tech: sửa thông tin domain (branding/links/email/livechat/app/payment)
      return ['owner', 'admin', 'agency', 'tech'].includes(this.$store.state.user.role);
    },
    emailMode() {
      return (this.formModel.email && this.formModel.email.mode) || 'callio';
    },
    senderStatus() {
      return (this.formModel.email && this.formModel.email.senderStatus) || 'none';
    },
    senderStatusLabel() {
      return { none: 'Chưa xác nhận', pending: 'Chờ xác nhận', confirmed: 'Đã xác nhận' }[this.senderStatus] || this.senderStatus;
    },
    senderBadgeClass() {
      return { none: 'badge-secondary', pending: 'badge-warning', confirmed: 'badge-success' }[this.senderStatus] || 'badge-secondary';
    },
    canCreate() {
      return ['owner', 'admin'].includes(this.$store.state.user.role);
    },
    canWrite() {
      return ['owner', 'admin', 'agency', 'tech'].includes(this.$store.state.user.role);
    },
    canDelete() {
      return ['owner'].includes(this.$store.state.user.role);
    }
  },
  i18n: {
    messages: {
      en: {
        all: 'All', status: 'Status',
        partnerDomain: {
          title: 'Partner domains', domain: 'Domain', domainLabel: 'Domain (FQDN)', name: 'Partner name',
          titleField: 'Display title', colors: 'Colors', enabled: 'Active', disabled: 'Inactive',
          enabledCheckbox: 'Active', clear: 'Remove', choosePngJpg: 'PNG/JPG only',
          logoHint: 'PNG/JPG, transparent background. Best ~200×60px, ≤ 2MB.',
          faviconHint: 'Square PNG/JPG, best 32×32 or 64×64px, ≤ 2MB.',
          colorsHint: 'Main overall colors of the CRM.',
          viewClients: 'Partner customers', agencyFilter: 'Filter by partner account',
          tabGeneral: 'General', tabBrand: 'Brand', tabLinks: 'Links', tabEmail: 'Email', tabLivechatApp: 'Livechat & App', tabPayment: 'Payment & Sharing',
          needAttention: 'Needs attention ({0})',
          warnNoAgency: 'No partner account assigned', warnSenderUnconfirmed: 'Email signature not confirmed → falls back to Callio',
          warnSmtpIncomplete: 'SMTP mode but config incomplete → falls back to Callio', warnNoOtpClient: 'Active but no OTP billing account → registration OTP will fail',
          warnNoOtpTemplate: 'Active with account but no OTP template selected → customers cannot register',
          warnNoAm: 'No AM assigned to care for customers', paymentReceive: 'Receiving account',
          secGeneral: 'General', secBrand: 'Brand identity', secLogin: 'Login page (white-label)', secColors: 'Dashboard colors',
          secDns: 'DNS setup (partner side)',
          dnsDesc: 'The partner creates ONE CNAME record at their DNS provider pointing the domain above to Callio:',
          dnsType: 'Type', dnsName: 'Name / Host', dnsValue: 'Target (value)', dnsCopy: 'Copy', dnsCopied: 'Copied', dnsCopyFailed: 'Copy failed',
          dnsNoteApex: 'Use a SUBDOMAIN (crm./cskh./app.partner.com). The root domain (partner.com) cannot take a CNAME — use CNAME flattening / ALIAS if required.',
          dnsNoteProxy: 'On Cloudflare, keep the record DNS only (grey cloud) — proxying it breaks certificate issuance (error 1014).',
          dnsNoteTls: 'The HTTPS certificate is issued automatically a few minutes after the CNAME resolves. Nothing to install on the partner side.',
          agency: 'Partner account', chooseAgency: 'Choose partner account', noAgency: 'No partner account',
          am: 'Account Manager (AM)', amPh: 'Select the AM in charge', amDesc: 'Customers registering / assigned to this domain are auto-assigned to this AM (client.account).',
          otpClient: 'Billing account for OTP (SMS/ZNS)', otpClientPh: 'Select the partner Callio account',
          otpClientDesc: 'The partner Callio account holding the approved SMS brandname / Zalo OA / OTP templates. Registration OTP on this domain is sent via that account and billed to it (allowed to go negative). Empty / not configured → OTP is NOT sent (no Callio fallback).',
          otpSmsTemplate: 'SMS OTP template', otpZnsTemplate: 'ZNS OTP template',
          otpSmsTemplateDesc: 'SMS template used to send registration OTP (must belong to the account above).',
          otpZnsTemplateDesc: 'Zalo ZNS OTP template (type = otp) used as fallback / alternative channel.',
          otpTemplateNone: '— None —', otpChooseClientFirst: '— Select the account first —',
          agencyRequired: 'Please choose a partner account (required)',
          agencyReadonlyNote: 'Domain & partner account are managed by Callio. You can edit branding and the domain settings below — not this section.',
          colDomain: 'Partner / Domain', colTheme: 'Theme', colConfig: 'Configuration', clientCount: 'Customers',
          loginBanner: 'Login banner image', loginBannerDesc: 'Right-side image on login/forgot/register (PNG/JPG, ≤3MB)',
          loginSlogan: 'Login slogan', loginSloganDesc: 'Description shown under the banner. Each line is one row.',
          primaryColor: 'Primary color', secondaryColor: 'Secondary color',
          sidebarBgColor: 'Sidebar background', headerBgColor: 'Header background',
          secLinks: 'Links & contact', linksDesc: 'Empty field → link hidden on partner domain (no Callio fallback).',
          lTerms: 'Terms of service URL', lPrivacy: 'Privacy policy URL', lGuide: 'User guide URL', lSupport: 'Support URL',
          lWebsite: 'Website URL', lHotline: 'Hotline', lContactEmail: 'Contact email', lPricing: 'Service pricing URL', appName: 'App name (PWA)',
          secEmail: 'Email branding', emailDesc: 'Branding for system/notification emails sent to the partner\'s customers (password reset, OTP, invoices...): sender name, logo, footer, company name/address. The sending address stays a Callio-verified address for inbox deliverability.',
          eFromName: 'From name', eFromAddress: 'Your sender email', eLogoUrl: 'Email logo URL', eCompanyName: 'Company name',
          eCompanyAddress: 'Company address', eFooter: 'Email footer (HTML)', eFooterPh: 'HTML footer for emails...',
          senderTitle: 'Send from your own address (Sender Signature)',
          senderGuide: 'Send system emails FROM the partner address for real:\n1. Enter the sender email above (CUSTOM DOMAIN, e.g. support@yourcompany.com — NOT Gmail/Yahoo/Hotmail, Postmark rejects those) → Save.\n2. Click "Send confirmation":\n  • If the domain is already verified → status turns "Confirmed" INSTANTLY, nothing else to do.\n  • If it is a new domain → Postmark emails that address; open it, click the confirmation link, then click "Check".\n(Empty / not confirmed → still sent from the Callio address, no interruption.)',
          senderSend: 'Send confirmation', senderCheck: 'Check', senderResend: 'Resend confirmation',
          senderSaveFirst: 'Save the partner domain first to manage the sender address.',
          sendMode: 'How to send system emails',
          modeCallio: 'Callio address (default) — sent from a Callio-verified address with partner branding. No setup.',
          modeSignature: 'Partner address via Postmark (Sender Signature) — needs a custom domain + DNS.',
          modeSmtp: 'Partner SMTP server — send through the partner\'s own mail server.',
          modeCallioHint: 'Emails are sent from the Callio address but carry the partner branding (logo/footer/sender name). Best deliverability, zero setup.',
          smtpTitle: 'Partner SMTP server',
          smtpHost: 'SMTP host', smtpPort: 'Port', smtpSecure: 'Encryption', smtpUser: 'Username (email)', smtpPass: 'Password',
          smtpPassSet: '•••••• (saved — leave blank to keep)', smtpPassShow: 'Show password', smtpPassHide: 'Hide password',
          smtpGuide: 'Enter the partner mail server info → Save → click "Send test".\nPort 465 = tick SSL; port 587/25 = leave SSL unticked (STARTTLS/plain).\nIf SMTP fails when sending, the system falls back to the Callio address so security emails are never lost.',
          smtpTest: 'Send test', smtpTestTo: 'Test recipient (default: you)',
          smtpTestSent: 'Test email sent to {0}. Check the inbox.',
          secLivechat: 'Livechat', livechatCode: 'Embed code (web)', livechatDesc: 'Embed the full chat widget (HTML/JS). If left empty, this domain will have NO livechat.',
          livechatAppUrl: 'Livechat link (mobile app)', livechatAppUrlDesc: 'URL the mobile app opens for support chat. Empty → no livechat in the app.',
          livechatPh: '<scr' + 'ipt>...</scr' + 'ipt>',
          secShare: 'Customer data sharing', shareLabel: 'Share customers with Callio',
          shareDesc: 'Unchecked: Callio sales cannot see customers of this domain and they are not pushed to Callio internal CRM.',
          secApp: 'Mobile app', appDesc: 'Partner app store links (their white-label app). If left empty, the "get the app" prompt is hidden on this domain.',
          appIos: 'App Store URL (iOS)', appAndroid: 'Google Play URL (Android)',
          appIosVersion: 'Latest iOS version', appAndroidVersion: 'Latest Android version',
          secSelfPurchase: 'Self-service purchasing',
          selfPurchaseLabel: 'Let customers buy and top up on their own',
          selfPurchaseDesc: 'Unticked: every buy / upgrade / renew / top-up button is hidden and replaced by a "Contact" button that opens the partner support chat. Enforced server-side too — customers of this domain cannot call the purchase APIs.',
          secPayment: 'Payment', paymentEnabled: 'Use partner\'s own bank account',
          paymentDesc: 'When enabled, the payment page shows the partner bank account + auto-generates a VietQR. Money goes to the partner — Callio does NOT auto-reconcile or top up credit.',
          paymentBank: 'Bank', paymentChooseBank: 'Choose bank', paymentBranch: 'Branch', paymentHolder: 'Account holder', paymentNumber: 'Account number',
          paymentPrefix: 'Transfer content prefix', paymentPrefixHint: 'Leave blank = "callio". No spaces/accents, max ~10 chars (e.g. "abc" → content "abc296981").'
        }
      },
      vi: {
        all: 'Tất cả', status: 'Trạng thái',
        partnerDomain: {
          title: 'Domain đối tác', domain: 'Domain', domainLabel: 'Domain (FQDN)', name: 'Tên đối tác',
          titleField: 'Tiêu đề hiển thị', colors: 'Màu sắc', enabled: 'Đang hoạt động', disabled: 'Ngừng hoạt động',
          enabledCheckbox: 'Được hoạt động', clear: 'Xoá', choosePngJpg: 'Chỉ PNG/JPG',
          logoHint: 'PNG/JPG, nền trong suốt. Kích thước tốt nhất ~200×60px, dung lượng ≤ 2MB.',
          faviconHint: 'PNG/JPG vuông, tốt nhất 32×32 hoặc 64×64px, dung lượng ≤ 2MB.',
          colorsHint: 'Màu sắc tổng quan chính của CRM.',
          viewClients: 'Khách hàng của đối tác', agencyFilter: 'Lọc theo tài khoản đối tác',
          tabGeneral: 'Chung', tabBrand: 'Thương hiệu', tabLinks: 'Liên kết', tabEmail: 'Email', tabLivechatApp: 'Livechat & App', tabPayment: 'Thanh toán & Chia sẻ',
          needAttention: 'Cần chú ý ({0})',
          warnNoAgency: 'Chưa gắn tài khoản đối tác', warnSenderUnconfirmed: 'Sender Signature chưa xác nhận → email vẫn gửi từ Callio',
          warnSmtpIncomplete: 'Chọn SMTP nhưng cấu hình thiếu → email vẫn gửi từ Callio', warnNoOtpClient: 'Đang hoạt động nhưng chưa gắn tài khoản OTP → OTP đăng ký sẽ lỗi',
          warnNoOtpTemplate: 'Đã gắn tài khoản nhưng chưa chọn template OTP → khách không đăng ký được',
          warnNoAm: 'Chưa gắn AM chăm sóc khách', paymentReceive: 'Tài khoản nhận tiền',
          secGeneral: 'Thông tin chung', secBrand: 'Nhận diện thương hiệu', secLogin: 'Trang đăng nhập (white-label)', secColors: 'Màu sắc dashboard',
          secDns: 'Trỏ DNS (đối tác tự làm)',
          dnsDesc: 'Đối tác vào nhà cung cấp DNS của mình, tạo MỘT bản ghi CNAME trỏ domain ở trên về Callio:',
          dnsType: 'Loại', dnsName: 'Tên / Host', dnsValue: 'Trỏ về (giá trị)', dnsCopy: 'Sao chép', dnsCopied: 'Đã chép', dnsCopyFailed: 'Không sao chép được',
          dnsNoteApex: 'Phải dùng SUBDOMAIN (crm./cskh./app.doitac.com). Domain gốc (doitac.com) không đặt CNAME được — nếu bắt buộc thì dùng CNAME flattening / ALIAS.',
          dnsNoteProxy: 'Nếu đối tác dùng Cloudflare: để bản ghi ở chế độ DNS only (mây xám) — bật proxy sẽ hỏng cấp chứng chỉ (lỗi 1014).',
          dnsNoteTls: 'Chứng chỉ HTTPS được cấp tự động vài phút sau khi CNAME có hiệu lực. Đối tác KHÔNG phải cài gì thêm.',
          agency: 'Tài khoản đối tác', chooseAgency: 'Chọn tài khoản đối tác', noAgency: 'Chưa gắn tài khoản',
          am: 'Nhân viên chăm sóc (AM)', amPh: 'Chọn AM phụ trách', amDesc: 'Khách đăng ký / phân công vào domain này sẽ tự gán cho AM này (client.account).',
          otpClient: 'Tài khoản ghi nhận OTP (SMS/ZNS)', otpClientPh: 'Chọn tài khoản Callio của đại lý',
          otpClientDesc: 'Tài khoản Callio của đại lý — nơi chứa SMS brandname / Zalo OA / template OTP đã duyệt. OTP đăng ký trên domain này gửi qua tài khoản đó và trừ cước vào đó (cho trừ âm). Để trống / chưa cấu hình → KHÔNG gửi OTP (không fallback Callio).',
          otpSmsTemplate: 'Template OTP SMS', otpZnsTemplate: 'Template OTP ZNS',
          otpSmsTemplateDesc: 'Template SMS dùng gửi OTP đăng ký (phải thuộc tài khoản ở trên).',
          otpZnsTemplateDesc: 'Template ZNS OTP (loại = otp) dùng làm kênh thay thế / dự phòng.',
          otpTemplateNone: '— Không chọn —', otpChooseClientFirst: '— Chọn tài khoản trước —',
          agencyRequired: 'Vui lòng chọn Tài khoản đối tác (bắt buộc)',
          agencyReadonlyNote: 'Domain & tài khoản đối tác do Callio quản lý. Bạn chỉnh được giao diện + các thông tin domain bên dưới — riêng phần này không sửa được.',
          colDomain: 'Đối tác / Domain', colTheme: 'Giao diện', colConfig: 'Cấu hình', clientCount: 'Khách hàng',
          loginBanner: 'Ảnh banner trang đăng nhập', loginBannerDesc: 'Ảnh cột phải trang login/forgot/register (PNG/JPG, ≤3MB)',
          loginSlogan: 'Câu giới thiệu', loginSloganDesc: 'Mô tả hiển thị dưới banner. Mỗi dòng là một hàng.',
          primaryColor: 'Màu chính', secondaryColor: 'Màu phụ',
          sidebarBgColor: 'Nền sidebar', headerBgColor: 'Nền header',
          secLinks: 'Liên kết & liên hệ', linksDesc: 'Field rỗng → ẩn link trên domain đối tác (KHÔNG hiện link Callio).',
          lTerms: 'Link Điều khoản dịch vụ', lPrivacy: 'Link Chính sách dữ liệu cá nhân', lGuide: 'Link Hướng dẫn sử dụng', lSupport: 'Link Hỗ trợ',
          lWebsite: 'Website', lHotline: 'Hotline', lContactEmail: 'Email liên hệ', lPricing: 'Link bảng giá dịch vụ', appName: 'Tên app (PWA)',
          secEmail: 'Email thương hiệu', emailDesc: 'Thương hiệu cho email hệ thống/thông báo gửi tới khách của đối tác (đặt lại mật khẩu, OTP, hoá đơn...): đổi tên người gửi, logo, footer, tên/địa chỉ công ty. Địa chỉ GỬI vẫn giữ địa chỉ Callio đã xác thực để email vào được inbox.',
          eFromName: 'Tên người gửi', eFromAddress: 'Email người gửi của bạn', eLogoUrl: 'URL logo email', eCompanyName: 'Tên công ty',
          eCompanyAddress: 'Địa chỉ công ty', eFooter: 'Footer email (HTML)', eFooterPh: 'HTML footer cho email...',
          senderTitle: 'Gửi thật từ địa chỉ của bạn (Sender Signature)',
          senderGuide: 'Để email hệ thống gửi THẬT từ địa chỉ đối tác:\n1. Nhập email người gửi vào ô ngay trên (TÊN MIỀN RIÊNG, vd hotro@congty.vn — KHÔNG dùng Gmail/Yahoo/Hotmail, Postmark sẽ từ chối) → bấm Lưu.\n2. Bấm "Gửi xác nhận":\n  • Nếu tên miền đã được xác thực sẵn → chuyển "Đã xác nhận" NGAY, không cần làm gì thêm.\n  • Nếu tên miền mới → Postmark gửi 1 email tới địa chỉ đó; mở mail bấm link xác nhận rồi bấm "Kiểm tra".\n(Để trống / chưa xác nhận → vẫn gửi từ địa chỉ Callio, không gián đoạn.)',
          senderSend: 'Gửi xác nhận', senderCheck: 'Kiểm tra', senderResend: 'Gửi lại xác nhận',
          senderSaveFirst: 'Lưu domain đối tác trước để quản lý địa chỉ gửi.',
          sendMode: 'Cách gửi email hệ thống',
          modeCallio: 'Địa chỉ Callio (mặc định) — gửi từ địa chỉ Callio đã xác thực, mang branding đối tác. Không cần cấu hình.',
          modeSignature: 'Địa chỉ đối tác qua Postmark (Sender Signature) — cần tên miền riêng + DNS.',
          modeSmtp: 'Mail server riêng của đối tác (SMTP) — gửi qua chính server của đối tác.',
          modeCallioHint: 'Email gửi từ địa chỉ Callio nhưng mang branding đối tác (logo/footer/tên người gửi). Deliverability tốt nhất, không cần cấu hình gì.',
          smtpTitle: 'Mail server riêng của đối tác (SMTP)',
          smtpHost: 'Máy chủ SMTP', smtpPort: 'Cổng', smtpSecure: 'Mã hoá', smtpUser: 'Tài khoản (email)', smtpPass: 'Mật khẩu',
          smtpPassSet: '•••••• (đã lưu — để trống nếu không đổi)', smtpPassShow: 'Hiện mật khẩu', smtpPassHide: 'Ẩn mật khẩu',
          smtpGuide: 'Nhập thông tin mail server của đối tác → Lưu → bấm "Gửi test".\nCổng 465 = tích SSL; cổng 587/25 = bỏ tích SSL (STARTTLS/thường).\nNếu SMTP lỗi lúc gửi, hệ thống tự fallback về địa chỉ Callio để email bảo mật không bị mất.',
          smtpTest: 'Gửi test', smtpTestTo: 'Email nhận test (mặc định: bạn)',
          smtpTestSent: 'Đã gửi email test tới {0}. Kiểm tra hộp thư.',
          secLivechat: 'Livechat', livechatCode: 'Code nhúng (web)', livechatDesc: 'Nhúng cả bộ widget chat (HTML/JS). Nếu để trống domain này sẽ KHÔNG có livechat.',
          livechatAppUrl: 'Link livechat (app mobile)', livechatAppUrlDesc: 'Link app mobile mở để chat hỗ trợ. Để trống → app không có livechat.',
          livechatPh: '<scr' + 'ipt>...</scr' + 'ipt>',
          secShare: 'Chia sẻ dữ liệu khách', shareLabel: 'Chia sẻ khách cho Callio',
          shareDesc: 'Bỏ tích: sales Callio KHÔNG thấy khách của domain này + KHÔNG đẩy lên CRM nội bộ Callio.',
          secApp: 'App mobile', appDesc: 'Link cài đặt app trên chợ (store app white-label) của đối tác. Nếu để trống sẽ ẩn lời mời tải app trên domain này.',
          appIos: 'Link App Store (iOS)', appAndroid: 'Link Google Play (Android)',
          appIosVersion: 'Version mới nhất iOS', appAndroidVersion: 'Version mới nhất Android',
          secSelfPurchase: 'Khách tự mua / tự thanh toán',
          selfPurchaseLabel: 'Cho phép khách tự mua và tự nạp tiền',
          selfPurchaseDesc: 'Bỏ tích: ẩn toàn bộ nút mua / nâng cấp / gia hạn / nạp tiền, thay bằng nút "Liên hệ" mở chat hỗ trợ của đối tác. Chặn cả ở máy chủ — khách của domain này không gọi được API mua hàng.',
          secPayment: 'Thanh toán', paymentEnabled: 'Dùng tài khoản ngân hàng riêng của đối tác',
          paymentDesc: 'Khi bật, trang thanh toán hiện tài khoản ngân hàng của đối tác + tự sinh mã QR VietQR. Tiền vào tài khoản đối tác — Callio KHÔNG tự đối soát/nạp credit.',
          paymentBank: 'Ngân hàng', paymentChooseBank: 'Chọn ngân hàng', paymentBranch: 'Chi nhánh', paymentHolder: 'Chủ tài khoản', paymentNumber: 'Số tài khoản',
          paymentPrefix: 'Prefix nội dung chuyển khoản', paymentPrefixHint: 'Để trống = "callio". Viết liền không dấu, ≤10 ký tự (VD "abc" → nội dung "abc296981").'
        }
      }
    }
  },
  methods: {
    onCopyDns: function () {
      this.dnsCopied = true;
      setTimeout(() => { this.dnsCopied = false; }, 2000);
    },
    onCopyDnsError: function () {
      this.$swal({ text: this.$t('partnerDomain.dnsCopyFailed'), icon: 'error', toast: true, position: 'top-end', timer: 2500, showConfirmButton: false });
    },
    // Bắt buộc chọn Tài khoản đối tác trước khi lưu (giữ modal mở nếu thiếu)
    onSave: function (evt) {
      // Chỉ owner/admin mới quản lý field "Tài khoản đối tác" → mới cần check bắt buộc.
      if (this.canEditMeta && !this.formModel.agency) {
        if (evt) evt.preventDefault();
        this.$swal({ text: this.$t('partnerDomain.agencyRequired'), icon: 'error' });
        return;
      }
      this.save(evt); // save() của mixin CRUD
    },
    // Chọn ngân hàng → lưu kèm tên hiển thị (Billing dùng để hiện, không cần danh sách bank)
    onBankChange: function () {
      const b = this.banks.find(x => x.code === this.formModel.payment.bankCode);
      this.formModel.payment.bankName = b ? b.name : '';
    },
    senderApi: function (method, action, okMsg) {
      this.senderActing = true;
      this.$service({ method, url: `/partner-domain/${this.formModel.id}/sender/${action}` }).then(res => {
        if (res.data && res.data.senderStatus) this.$set(this.formModel.email, 'senderStatus', res.data.senderStatus);
        this.$swal({ text: okMsg, icon: 'success', toast: true, position: 'top-end', timer: 3000, showConfirmButton: false });
      }).catch(error => {
        const msg = error.response && error.response.data && (error.response.data.message || error.response.data.msg);
        this.$swal({ text: msg || 'Có lỗi xảy ra', icon: 'error' });
      }).finally(() => { this.senderActing = false; });
    },
    senderConfirm: function () { this.senderApi('post', 'confirm', 'Đã gửi email xác nhận tới địa chỉ này. Mở hộp thư và bấm link xác nhận.'); },
    senderCheck: function () { this.senderApi('get', 'status', 'Đã cập nhật trạng thái.'); },
    senderResend: function () { this.senderApi('post', 'resend', 'Đã gửi lại email xác nhận.'); },
    // Test SMTP đối tác — gửi email thật qua server đối tác đã LƯU (phải Lưu trước)
    smtpTest: function () {
      this.smtpTesting = true; this.smtpTestMsg = '';
      this.$service.post(`/partner-domain/${this.formModel.id}/email/smtp-test`, { testTo: this.smtpTestTo || undefined }).then(res => {
        this.smtpTestOk = true;
        this.smtpTestMsg = this.$t('partnerDomain.smtpTestSent', [(res.data && res.data.to) || '']);
      }).catch(error => {
        this.smtpTestOk = false;
        this.smtpTestMsg = (error.response && error.response.data && (error.response.data.msg || error.response.data.message)) || 'Có lỗi xảy ra';
      }).finally(() => { this.smtpTesting = false; });
    },
    // Trả danh sách màu đã set (tránh v-for+v-if trên cùng element)
    activeColors: function (item) {
      if (!item || !item.branding) return [];
      return this.colorFields.filter(c => item.branding[c]);
    },
    emailModeShort: function (item) {
      const m = (item.email && item.email.mode) || 'callio';
      return { callio: 'Callio', signature: 'Sig', smtp: 'SMTP' }[m] || 'Callio';
    },
    emailModeLabel: function (item) {
      const m = (item.email && item.email.mode) || 'callio';
      return { callio: this.$t('partnerDomain.modeCallio'), signature: this.$t('partnerDomain.modeSignature'), smtp: this.$t('partnerDomain.modeSmtp') }[m] || 'Callio';
    },
    // Cảnh báo cấu hình có VẤN ĐỀ THẬT (âm thầm hỏng) — để admin sửa. Trả mảng thông báo.
    configWarnings: function (item) {
      const w = [];
      const e = item.email || {};
      if (!item.agency) w.push(this.$t('partnerDomain.warnNoAgency'));
      if (e.mode === 'signature' && e.senderStatus !== 'confirmed') w.push(this.$t('partnerDomain.warnSenderUnconfirmed'));
      if (e.mode === 'smtp' && (!e.smtp || !e.smtp.host || !e.smtp.user || !e.smtp.hasPass)) w.push(this.$t('partnerDomain.warnSmtpIncomplete'));
      // Domain đang hoạt động (cho đăng ký) nhưng chưa gắn tài khoản OTP → OTP đăng ký sẽ lỗi
      if (item.enabled && !item.client) w.push(this.$t('partnerDomain.warnNoOtpClient'));
      // Có tài khoản nhưng chưa chọn template OTP kênh nào → khách KHÔNG đăng ký được
      else if (item.enabled && item.client && (!item.otp || (!item.otp.smsTemplate && !item.otp.znsTemplate))) {
        w.push(this.$t('partnerDomain.warnNoOtpTemplate'));
      }
      return w;
    },
    // Nạp template OTP (SMS + ZNS otp) của client đại lý cho 2 dropdown. Bỏ chọn template không thuộc client hiện tại.
    loadOtpTemplates: function (clientId) {
      if (!clientId) {
        this.otpSmsTemplates = [];
        this.otpZnsTemplates = [];
        return;
      }
      this.otpTemplatesLoading = true;
      this.$service.get('/partner-domain/otp-templates', { params: { client: clientId } }).then(response => {
        const data = response.data || {};
        this.otpSmsTemplates = data.smsTemplates || [];
        this.otpZnsTemplates = data.znsTemplates || [];
        if (this.formModel.otp) {
          if (this.formModel.otp.smsTemplate && !this.otpSmsTemplates.some(t => t._id === this.formModel.otp.smsTemplate)) {
            this.formModel.otp.smsTemplate = null;
          }
          if (this.formModel.otp.znsTemplate && !this.otpZnsTemplates.some(t => t._id === this.formModel.otp.znsTemplate)) {
            this.formModel.otp.znsTemplate = null;
          }
        }
      }).catch(() => {
        this.otpSmsTemplates = [];
        this.otpZnsTemplates = [];
      }).finally(() => { this.otpTemplatesLoading = false; });
    },
    load: function () {
      let loader = this.$loading.show();
      this.listLoading = true;
      this.$service.get(this.service, { params: this.filterFormModel }).then(response => {
        this.apply(response.data);
      }).catch(error => {
        if (error.response && error.response.data && error.response.data.message) {
          this.$swal({ text: error.response.data.message, icon: 'error' });
        }
      }).finally(() => {
        loader.hide();
        this.listLoading = false;
      });
    },
    create: function () {
      // Override để branding luôn là object MỚI (tránh share reference giữa các lần tạo)
      this.formModel = {
        domain: null,
        name: null,
        agency: null,
        client: null,
        am: null,
        otp: { smsTemplate: null, znsTemplate: null },
        enabled: true,
        appName: '',
        shareCustomersWithCallio: true,
        selfPurchase: true,
        branding: emptyBranding(),
        links: emptyLinks(),
        email: emptyEmail(),
        livechat: { embedCode: '', appUrl: '' },
        app: emptyApp(),
        payment: emptyPayment()
      };
      this.otpSmsTemplates = [];
      this.otpZnsTemplates = [];
      this.formTitle = this.$t('create');
      this.formErrors = [];
      this.showForm = true;
    },
    update: function (id) {
      let loader = this.$loading.show();
      this.$service.get(this.service + '/' + id).then(response => {
        let data = response.data || {};
        // Đảm bảo các object tồn tại đủ field cho data cũ
        data.branding = Object.assign(emptyBranding(), data.branding || {});
        data.links = Object.assign(emptyLinks(), data.links || {});
        data.email = Object.assign(emptyEmail(), data.email || {});
        data.livechat = Object.assign({ embedCode: '', appUrl: '' }, data.livechat || {});
        data.app = Object.assign(emptyApp(), data.app || {});
        data.payment = Object.assign(emptyPayment(), data.payment || {});
        data.otp = Object.assign({ smsTemplate: null, znsTemplate: null }, data.otp || {});
        if (data.appName === undefined) data.appName = '';
        if (data.shareCustomersWithCallio === undefined) data.shareCustomersWithCallio = true;
        this.formModel = data;
        this.formModel.id = id;
        this.formTitle = this.$t('update');
        this.formErrors = [];
        this.showForm = true;
      }).finally(() => { loader.hide(); });
    },
    uploadAsset: function (file, field, maxMb) {
      if (!file) return;
      maxMb = maxMb || 2;
      if (file.size / (1024 * 1024) > maxMb) {
        this.$swal({ text: this.$t('partnerDomain.choosePngJpg') + ' (≤ ' + maxMb + 'MB)', icon: 'error' });
        return;
      }
      let loader = this.$loading.show();
      const formData = new FormData();
      formData.append('file', file);
      this.$service.post('/upload/upload-image', formData).then(response => {
        const arr = response.data;
        if (Array.isArray(arr) && arr.length && arr[0].url) {
          this.$set(this.formModel.branding, field, arr[0].url);
        }
      }).catch(error => {
        const msg = error.response && error.response.data && error.response.data.message;
        this.$swal({ text: msg || 'Upload failed', icon: 'error' });
      }).finally(() => { loader.hide(); });
    }
  }
};
</script>

<style scoped>
.pd-form .pd-section {
  border: 1px solid #ececec;
  border-radius: 8px;
  padding: 16px 16px 4px;
  margin-bottom: 18px;
  background: #fcfcfd;
}
.pd-form .pd-dns__row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  margin-bottom: 10px;
}
.pd-form .pd-dns__cell {
  display: flex;
  align-items: center;
  gap: 6px;
}
.pd-form .pd-dns__cell--grow { flex: 1 1 auto; }
.pd-form .pd-dns__label {
  font-size: 12px;
  color: #8a8f98;
  text-transform: uppercase;
}
.pd-form .pd-dns code {
  background: #eef1f5;
  color: #2c3e50;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 13px;
}
.pd-form .pd-section__title {
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: #6c5ce7;
  margin: -4px 0 14px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}
.pd-form .pd-section__title svg { margin-right: 6px; }
.pd-agency-filter >>> .form-group { margin-bottom: 0; }

/* ── List cards ── */
.pd-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(430px, 1fr)); gap: 16px; }
.pd-card {
  background: #fff; border: 1px solid #ececf1; border-radius: 12px; overflow: hidden;
  display: flex; flex-direction: column; transition: box-shadow .15s;
}
.pd-card:hover { box-shadow: 0 6px 22px rgba(60,60,90,.08); }
.pd-card__head { display: flex; align-items: center; gap: 12px; padding: 16px; border-bottom: 1px solid #f2f2f5; }
.pd-card__title { min-width: 0; }
.pd-card__body { padding: 15px 16px; display: grid; grid-template-columns: 1fr 1fr; gap: 12px 18px; flex: 1; }
.pd-field { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.pd-field--wide { grid-column: 1 / -1; }
.pd-field__label { font-size: 10.5px; text-transform: uppercase; letter-spacing: .03em; color: #a5a5b0; font-weight: 700; }
.pd-field__label svg { margin-right: 4px; }
.pd-field__val { font-size: 13px; color: #2d3436; word-break: break-word; }
.pd-link { color: #0984e3; font-weight: 700; }
.pd-link:hover { color: #0872c4; text-decoration: none; }
.pd-card__foot { display: flex; align-items: center; padding: 12px 16px; border-top: 1px solid #f2f2f5; background: #fafafb; }
.pd-logo-box {
  width: 48px; height: 48px; border: 1px solid #ececf1; border-radius: 10px;
  display: flex; align-items: center; justify-content: center; background: #fff; margin-right: 12px; flex-shrink: 0;
}
.pd-logo-box--lg { width: 60px; height: 60px; border-radius: 12px; margin-right: 0; }
.pd-logo-box img { max-width: 40px; max-height: 40px; object-fit: contain; }
.pd-logo-box--lg img { max-width: 50px; max-height: 50px; }
.pd-name { font-weight: 700; font-size: 14px; color: #2d3436; line-height: 1.25; }
.pd-domain-link { font-size: 12px; color: #6c5ce7; }
.pd-domain-link:hover { text-decoration: underline; }
.pd-account { font-size: 13px; }
.pd-client-count {
  display: inline-flex; align-items: center; gap: 5px; font-weight: 700; font-size: 14px;
  color: #0984e3; background: #eaf4fd; padding: 5px 13px; border-radius: 20px;
}
.pd-client-count:hover { background: #d6ebfb; color: #0984e3; text-decoration: none; }
.pd-chip {
  display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600;
  padding: 3px 9px; border-radius: 20px; background: #f0f0f5; color: #555; white-space: nowrap;
}
.pd-chip--email { background: #eef2ff; color: #4f46e5; }
.pd-chip--pay   { background: #ecfdf5; color: #059669; }
.pd-chip--app   { background: #fff7ed; color: #ea580c; }
.pd-chip--chat  { background: #eff6ff; color: #2563eb; }
.pd-chip--share { background: #f0fdf4; color: #16a34a; }
.pd-swatch { width: 14px; height: 14px; border-radius: 4px; border: 1px solid #ddd; }
.pd-status {
  display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600;
  padding: 4px 13px; border-radius: 20px;
}
.pd-status--on  { background: #eafaf1; color: #27ae60; }
.pd-status--off { background: #f1f2f6; color: #95a5a6; }
.pd-status-dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
.pd-warn {
  display: inline-flex; align-items: center; gap: 5px; margin-top: 7px;
  font-size: 11px; font-weight: 600; color: #d35400; background: #fef5e7;
  padding: 3px 10px; border-radius: 20px; cursor: help;
}
</style>
